/**
 * `/partidas/:gameId` — cómo ha ido la partida.
 *
 * **El dato principal de esta pantalla no es la clasificación**, y lo dice el propio documento
 * maestro (§11): es el **error compartido**, la opción incorrecta que eligió más del 40 % de la
 * clase. Una clasificación dice quién ganó; un error compartido dice qué hay que volver a explicar.
 * Por eso se señala con etiqueta y explicación, no con un color más entre otros.
 *
 * Todo lo que se ve aquí lo autoriza la base de datos: son datos de menores identificables uno a
 * uno, y las políticas ya impiden que los lea quien no alojó la partida.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import type { GameReport, ParticipantReport, QuestionReport } from '@clica/shared';
import { fetchAnalytics, type GameAnalytics } from '../analytics/api.js';
import type { SessionProfile } from '@clica/shared';
import { ContenidoMarkdown } from '../markdown/contenido.js';
import { Boton } from '../components/sistema/boton.js';
import { Marco } from '../components/sistema/marco.js';
import { useRecurso, VistaRecurso } from '../components/sistema/recurso.js';

export function PantallaAnaliticas({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const { gameId = '' } = useParams();

  const recurso = useRecurso<GameAnalytics>(
    useCallback(() => fetchAnalytics(gameId), [gameId]),
    [gameId],
  );

  useEffect(() => {
    document.title = `${t('analytics.title')} · ${t('app.name')}`;
  }, [t]);

  return (
    <Marco
      profile={profile}
      migas={[{ texto: t('subjects.title'), a: '/asignaturas' }, { texto: t('analytics.title') }]}
    >
      <main id="contenido" className="mx-auto w-full max-w-5xl p-6">
        <h1 className="font-display text-h1 font-extrabold text-balance">{t('analytics.title')}</h1>

        {/* Antes, «no existe», «no es tuya» y «ha fallado la red» daban el mismo mensaje sin
            reintentar ni enlace a ninguna parte: un callejón. */}
        <VistaRecurso recurso={recurso}>
          {(datos) => (
            <>
              <p className="text-lead text-text-dim mt-1">{datos.title}</p>

              {datos.report.totalAnswers === 0 ? (
                <p className="text-lead mt-8">{t('analytics.empty')}</p>
              ) : (
                <>
                  <p className="font-display text-h2 mt-6 font-bold tabular-nums">
                    {t('analytics.overall')}: {porcentaje(datos.report.overallAccuracy)}
                  </p>

                  <PorPregunta report={datos.report} />
                  <PorAlumno participants={datos.report.participants} />
                </>
              )}
            </>
          )}
        </VistaRecurso>

        <Boton variante="secundaria" className="mt-10" onClick={() => window.history.back()}>
          {t('analytics.back')}
        </Boton>
      </main>
    </Marco>
  );
}

/**
 * Las marcas decorativas.
 *
 * Van al lado de la palabra que dice lo mismo, nunca solas: en escala de grises la flecha y el aspa
 * siguen distinguiendo la mejor de la peor, y quien usa lector de pantalla lee el texto.
 */
const BEST_MARK = '\u25B2\u00a0';
const WORST_MARK = '\u25BC\u00a0';
const CORRECT_MARK = '\u2713\u00a0';

/** Nulo se dice, no se pinta como cero: «nadie respondió» no es «todos fallaron». */
function porcentaje(valor: number | null): string {
  return valor === null ? '—' : `${String(Math.round(valor * 100))} %`;
}

type OrdenPreguntas = 'position' | 'accuracy' | 'time';

const ORDENES_PREGUNTA = [
  { clave: 'position', etiqueta: 'analytics.sortByOrder' },
  { clave: 'accuracy', etiqueta: 'analytics.sortByAccuracy' },
  { clave: 'time', etiqueta: 'analytics.sortByTime' },
] as const;

function PorPregunta({ report }: { report: GameReport }): React.JSX.Element {
  const { t } = useTranslation();
  const [orden, setOrden] = useState<OrdenPreguntas>('position');

  const ordenadas = [...report.questions].sort((a, b) => {
    if (orden === 'accuracy') return (a.accuracy ?? 2) - (b.accuracy ?? 2);
    if (orden === 'time') return (b.averageMs ?? -1) - (a.averageMs ?? -1);
    return a.position - b.position;
  });

  return (
    <section aria-labelledby="preguntas-titulo" className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="preguntas-titulo" className="text-2xl font-semibold">
          {t('analytics.questionsTitle')}
        </h2>
        {/*
         * Las etiquetas dicen «por orden», no «orden»: hay otra tabla más abajo que también se
         * ordena por aciertos, y dos botones llamados igual en la misma pantalla son ambiguos
         * también para quien la mira, no solo para una prueba.
         */}
        <div className="flex gap-2 text-sm">
          {ORDENES_PREGUNTA.map(({ clave, etiqueta }) => (
            <button
              key={clave}
              type="button"
              onClick={() => setOrden(clave)}
              aria-pressed={orden === clave}
              className={`rounded border px-3 py-1 ${
                orden === clave ? 'border-edge' : 'border-line'
              }`}
            >
              {t(etiqueta)}
            </button>
          ))}
        </div>
      </div>

      <ul className="mt-4 flex flex-col gap-4">
        {ordenadas.map((question) => (
          <FilaPregunta
            key={question.questionId}
            question={question}
            best={question.questionId === report.bestQuestionId}
            worst={question.questionId === report.worstQuestionId}
          />
        ))}
      </ul>
    </section>
  );
}

function FilaPregunta({
  question,
  best,
  worst,
}: {
  question: QuestionReport;
  best: boolean;
  worst: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <li className="border-line rounded-xl border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-text-dim text-sm">
          {t('analytics.position', { index: question.position + 1 })}
        </span>
        <span className="flex flex-wrap gap-2 text-sm">
          {/* La mejor y la peor llevan símbolo y palabra, no solo color (principio IV). */}
          {best ? (
            <span className="text-ok">
              <span aria-hidden="true">{BEST_MARK}</span>
              {t('analytics.best')}
            </span>
          ) : null}
          {worst ? (
            <span className="text-ko">
              <span aria-hidden="true">{WORST_MARK}</span>
              {t('analytics.worst')}
            </span>
          ) : null}
        </span>
      </div>

      <ContenidoMarkdown source={question.promptMd} enLinea className="prose-clica mt-2" />

      <p className="mt-3 flex flex-wrap gap-6 text-lg tabular-nums">
        <span>
          {t('analytics.accuracy')}: <strong>{porcentaje(question.accuracy)}</strong>
        </span>
        <span className="text-text-dim">
          {t('analytics.averageTime')}:{' '}
          {question.averageMs === null
            ? '—'
            : t('analytics.seconds', { seconds: (question.averageMs / 1000).toFixed(1) })}
        </span>
      </p>

      {question.answered === 0 ? (
        <p className="text-text-dim mt-3">{t('analytics.notPlayed')}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {question.options.map((option) => (
            <li key={option.optionId} className="flex items-center gap-3">
              <span className="w-12 shrink-0 text-right tabular-nums">{option.count}</span>
              <span className="bg-surface h-6 flex-1 overflow-hidden rounded">
                <span
                  aria-hidden="true"
                  className={`block h-full rounded ${option.correct ? 'bg-ok' : 'bg-surface-hi'}`}
                  style={{ width: `${String(Math.round(option.share * 100))}%` }}
                />
              </span>
              <ContenidoMarkdown
                source={option.textMd}
                enLinea
                className="min-w-0 flex-1 truncate"
              />
              {option.correct ? (
                <span className="text-ok shrink-0 text-sm font-semibold">
                  <span aria-hidden="true">{CORRECT_MARK}</span>
                  {t('game.histogram.correct')}
                </span>
              ) : null}
              {option.distractor ? (
                <span
                  className="border-edge text-text shrink-0 rounded border-2 px-2 py-0.5 text-sm font-semibold"
                  title={t('analytics.distractorHelp')}
                >
                  <span aria-hidden="true">! </span>
                  {t('analytics.distractor')}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {question.hasDistractor ? (
        <p className="text-text-dim mt-3 text-sm">{t('analytics.distractorHelp')}</p>
      ) : null}
    </li>
  );
}

type OrdenAlumnos = 'name' | 'correct' | 'points' | 'unanswered';

function PorAlumno({
  participants,
}: {
  participants: readonly ParticipantReport[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const [orden, setOrden] = useState<OrdenAlumnos>('points');

  const ordenados = [...participants].sort((a, b) => {
    if (orden === 'name') return a.displayName.localeCompare(b.displayName);
    if (orden === 'correct') return b.correct - a.correct;
    if (orden === 'unanswered') return b.unanswered - a.unanswered;
    return b.points - a.points;
  });

  const COLUMNAS = [
    { clave: 'name', etiqueta: 'analytics.name' },
    { clave: 'correct', etiqueta: 'analytics.correctCount' },
    { clave: 'unanswered', etiqueta: 'analytics.unanswered' },
    { clave: 'points', etiqueta: 'analytics.points' },
  ] as const;

  return (
    <section aria-labelledby="alumnos-titulo" className="mt-12">
      <h2 id="alumnos-titulo" className="text-2xl font-semibold">
        {t('analytics.peopleTitle')}
      </h2>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-line border-b">
              {COLUMNAS.map((columna) => (
                <th key={columna.clave} scope="col" className="p-2">
                  <button
                    type="button"
                    onClick={() => setOrden(columna.clave)}
                    aria-pressed={orden === columna.clave}
                    className="underline-offset-4 hover:underline"
                  >
                    {t(columna.etiqueta)}
                  </button>
                </th>
              ))}
              <th scope="col" className="p-2">
                {t('analytics.alias')}
              </th>
              <th scope="col" className="p-2">
                {t('analytics.averageTime')}
              </th>
            </tr>
          </thead>
          <tbody>
            {ordenados.map((fila) => (
              <tr key={fila.participantId} className="border-line border-b">
                <td className="p-2">{fila.displayName}</td>
                <td className="p-2 tabular-nums">
                  {fila.correct} / {fila.answered}
                </td>
                <td className="p-2 tabular-nums">{fila.unanswered}</td>
                <td className="p-2 tabular-nums">{fila.points}</td>
                <td className="text-text-dim p-2">{fila.alias ?? '—'}</td>
                <td className="p-2 tabular-nums">
                  {fila.averageMs === null
                    ? '—'
                    : t('analytics.seconds', { seconds: (fila.averageMs / 1000).toFixed(1) })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
